import { clsx } from 'clsx'

import type { AnalyticsResponse } from '@tribeplatform/gql-client/types'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import type {
  GroupBy,
  ChartTimeFrame,
  ChartTypes,
  ScoreCharts,
  TinyCharts,
  PrimaryCharts,
} from '../types/index.js'

import { AllTimeScore } from './AllTimeScore.js'
import { ChartTitle } from './ChartTitle.js'
import { DataVisualizer } from './DataVisualizer.js'
import { Score } from './Score.js'

interface GeneralChart {
  id?: string
  type: ChartTypes
  title: string
  description?: string
  toolTipText?: string
  isLoading: boolean
  className?: string
}
interface ScoreChart extends GeneralChart {
  type: ScoreCharts
  current: number | string
  previous?: number | string
  total?: number | string
}
type GeneralScoreChart = Partial<Omit<ScoreChart, 'type'>>
interface GraphChart extends GeneralScoreChart {
  type: PrimaryCharts
  data: AnalyticsResponse
  timeFrame?: ChartTimeFrame
  groupBy?: GroupBy[]
  indexBy: string[]
  labels: string[]
  ColorTheme?: string[]
  syncId?: string
}

interface TinyChart extends Omit<GraphChart, 'type' | 'labels'> {
  type: TinyCharts
  labels?: string[]
}

type Props = ScoreChart | TinyChart | GraphChart

export const Chart = (props: Props) => {
  const isLoadingOrHasError =
    props.isLoading ||
    ('total' in props &&
      props.type === 'CumulativeTinyArea' &&
      !props.current) ||
    ('current' in props && !('total' in props) && !props.current) ||
    (props.type !== 'Score' && !props.data)

  return (
    <Card
      className={clsx(
        'p-4',
        props?.className,
        props.isLoading && 'animate-pulse bg-surface-pressed',
        isLoadingOrHasError && !props.isLoading && 'opacity-70',
      )}
    >
      <div className="h-full flex flex-col justify-start">
        <ChartTitle
          title={props?.title}
          description={props?.description}
          toolTipText={props?.toolTipText}
          isLoading={isLoadingOrHasError}
        />
        <div
          className={
            props.type === 'TinyArea' || props.type === 'CumulativeTinyArea'
              ? 'flex flex-row justify-between items-center'
              : ''
          }
        >
          {'total' in props && props.type === 'CumulativeTinyArea' && (
            <div className="flex-1">
              <AllTimeScore
                total={props.total}
                current={props.current}
                timeFrame={props.timeFrame}
                isLoading={isLoadingOrHasError}
              />
            </div>
          )}
          {'current' in props && !('total' in props) && (
            <div className="flex-1">
              <Score
                current={props.current}
                previous={props?.previous}
                isLoading={isLoadingOrHasError}
              />
            </div>
          )}
          {props.type !== 'Score' && (
            <div className="flex-1">
              <DataVisualizer
                type={props.type}
                data={props.data}
                timeFrame={props.timeFrame}
                indexBy={props.indexBy}
                labels={props.labels}
                groupBy={props?.groupBy}
                ColorTheme={props?.ColorTheme}
                syncId={props?.syncId}
                id={props?.id}
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
